import React, { useState, useRef } from "react";
import { IoCloseSharp } from "react-icons/io5";
import DOMPurify from "dompurify";
import "react-quill-new/dist/quill.snow.css";
import { 
  FiCalendar, 
  FiEye, 
  FiEdit2, 
  FiGlobe, 
  FiCopy, 
  FiCheck, 
  FiArrowUp, 
  FiBookOpen, 
  FiTarget 
} from "react-icons/fi";
import { toast } from "react-toastify";
import Button from "../../../components/ui/Button";
import { cleanTextContent, resolveImageUrl } from "./seoHelpers";

const BlogPreviewModal = ({ isOpen, onClose, onEdit, blog, loading = false }) => {
  const [copied, setCopied] = useState(false);
  const scrollContainerRef = useRef(null);

  if (!isOpen) return null;

  const formatDate = (isoString) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return isoString;
    }
  };

  const handleCopyLink = () => {
    const slug = blog?.slug || "";
    const url = slug ? `${window.location.origin}/blog/${slug}` : window.location.href;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
    }
    setCopied(true);
    toast.success("Article link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Determine if there is a distinct lead paragraph (metaDescription/excerpt)
  // that is NOT a duplicate of the first sentence of the content
  const getLeadText = () => {
    const raw = blog?.metaDescription || blog?.excerpt || blog?.seo?.metaDescription || "";
    if (!raw) return "";

    const cleaned = cleanTextContent(raw)
      .replace(/&[a-zA-Z0-9]{1,6}$/, "")
      .trim();

    if (!cleaned) return "";

    const cleanBodyStart = cleanTextContent(blog?.content || "");
    if (cleanBodyStart) {
      const sample = cleaned.slice(0, Math.min(50, cleaned.length));
      if (cleanBodyStart.startsWith(sample)) {
        return "";
      }
    }

    return cleaned;
  };

  const renderContent = (content = "") => {
    if (!content) {
      return (
        <div className="py-12 text-center text-gray-400 dark:text-gray-500 italic text-sm">
          No content written for this blog yet.
        </div>
      );
    }

    // Normalize non-breaking spaces so words wrap naturally across lines without chopping
    const normalized = content.replace(/&nbsp;/g, " ").replace(/\u00a0/g, " ");
    const cleanHtml = DOMPurify.sanitize(normalized);

    return (
      <div className="ql-snow blog-reader-content">
        <div 
          className="ql-editor !p-0 !min-h-0 !h-auto !overflow-visible select-text"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      </div>
    );
  };

  const getStatusBadge = (status = "Published") => {
    switch (status) {
      case "Published":
        return {
          pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
          dot: "bg-emerald-500",
          label: "Published"
        };
      case "Scheduled":
        return {
          pill: "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800",
          dot: "bg-blue-500",
          label: "Scheduled"
        };
      default:
        return {
          pill: "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800",
          dot: "bg-amber-500",
          label: "Draft"
        };
    }
  };

  const statusBadge = getStatusBadge(blog?.status);
  const leadText = getLeadText();
  const rawCover = blog?.coverImage || blog?.image;
  const coverSrc = resolveImageUrl(rawCover);
  const authorName = typeof blog?.author === "string"
    ? blog.author.trim()
    : (blog?.author?.name ? blog.author.name.trim() : "");
  const authorRole = typeof blog?.author === "object" && blog?.author?.role
    ? blog.author.role.trim()
    : "";
  const authorAvatar = typeof blog?.author === "object"
    ? blog?.author?.avatar
    : null;

  const keywordsList = Array.isArray(blog?.keywords)
    ? blog.keywords
    : (typeof blog?.keywords === "string" && blog.keywords
      ? blog.keywords.split(",").map((k) => k.trim()).filter(Boolean)
      : (Array.isArray(blog?.tags) ? blog.tags : []));

  const focusKeyword = blog?.seo?.focusKeyword;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5 md:p-6 animate-fadeIn">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-3xl shadow-2xl border border-gray-200 dark:border-slate-800 flex flex-col max-h-[92vh] overflow-hidden animate-slideUp">
        
        {/* Clean, Professional Header matching Super Admin design standard */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-primary/10 rounded-lg shrink-0">
              <FiBookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                  Blog Details
                </h2>
                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border inline-flex items-center gap-1.5 ${statusBadge.pill}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
                  <span>{statusBadge.label}</span>
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Preview article content and publishing information
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {blog?.slug && (
              <button
                type="button"
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-primary bg-gray-50 hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700 transition-colors"
                title="Copy blog URL"
              >
                {copied ? <FiCheck className="w-3.5 h-3.5 text-emerald-500" /> : <FiCopy className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{copied ? "Copied" : "Copy Link"}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg"
              title="Close"
            >
              <IoCloseSharp className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Reader Article Content */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-6 py-8 sm:px-8 scrollbar-thin">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-80 gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium animate-pulse">
                Loading blog details...
              </p>
            </div>
          ) : !blog ? null : (
            <article className="w-full">
              
              {/* Main Headline */}
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight mb-4">
                {blog.title}
              </h1>

              {/* Unified Author & Metadata Card */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-gray-50 dark:bg-slate-800/50 border border-gray-100 dark:border-slate-800/80 mb-6">
                <div className="flex items-center gap-3 min-w-0">
                  {authorAvatar ? (
                    <img
                      src={authorAvatar}
                      alt={authorName || "Author"}
                      className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-slate-700 shadow-2xs shrink-0"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center border border-primary/20 shadow-2xs shrink-0">
                      {(authorName || "P").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white leading-tight truncate">
                      {authorName || "Peoplesync Author"}
                      {authorRole && (
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
                          • {authorRole}
                        </span>
                      )}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1 flex-wrap">
                      {blog.publishedAt && (
                        <span className="flex items-center gap-1">
                          <FiCalendar className="w-3 h-3 text-gray-400" />
                          {formatDate(blog.publishedAt)}
                        </span>
                      )}
                      {blog.publishedAt && blog.views !== undefined && <span>•</span>}
                      {blog.views !== undefined && (
                        <span className="flex items-center gap-1">
                          <FiEye className="w-3 h-3 text-gray-400" />
                          {(blog.views || 0).toLocaleString()} views
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {blog.category && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shrink-0">
                      {blog.category}
                    </span>
                  )}
                  {blog.slug && (
                    <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-mono bg-white dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-slate-700 truncate max-w-[220px]">
                      <FiGlobe className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="truncate">/blog/{blog.slug}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Cover Hero Banner (Only if available) */}
              {coverSrc && (
                <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden mb-8 shadow-xs border border-gray-200/80 dark:border-slate-800 bg-gray-100 dark:bg-slate-800">
                  <img
                    src={coverSrc}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              {/* Lead Excerpt (only if distinct and cleaned) */}
              {leadText && (
                <div className="border-l-4 border-primary pl-4 py-2 mb-6 text-base text-gray-600 dark:text-gray-300 font-normal leading-relaxed italic bg-primary/5 dark:bg-primary/10 rounded-r-xl">
                  {leadText}
                </div>
              )}

              {/* Article Content */}
              <div className="text-gray-800 dark:text-gray-200 select-text">
                {renderContent(blog.content)}
              </div>

              {/* Keywords & SEO Metadata Section */}
              {(keywordsList.length > 0 || focusKeyword) && (
                <div className="mt-10 p-5 rounded-xl bg-gray-50 dark:bg-slate-800/40 border border-gray-200/80 dark:border-slate-800 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <FiTarget className="w-3.5 h-3.5 text-primary" />
                    <span>SEO & Metadata Information</span>
                  </div>

                  {focusKeyword && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Focus Keyword:
                      </span>
                      <span className="px-2.5 py-0.5 rounded-md bg-primary/10 text-primary font-semibold border border-primary/20">
                        {focusKeyword}
                      </span>
                    </div>
                  )}

                  {keywordsList.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-600 dark:text-gray-400 font-medium block mb-1.5">
                        Keywords / Tags:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {keywordsList.map((tag, i) => (
                          <span
                            key={i}
                            className="px-2.5 py-0.5 rounded-md text-xs font-medium bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-600 shadow-2xs"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </article>
          )}
        </div>

        {/* Clean, Non-Redundant Footer */}
        <div className="px-6 py-3.5 border-t border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={scrollToTop}
            className="text-xs text-gray-400 hover:text-primary transition-colors flex items-center gap-1.5 font-medium"
          >
            <FiArrowUp className="w-3.5 h-3.5" />
            <span>Back to Top</span>
          </button>

          <div className="flex items-center gap-2.5">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs px-4"
            >
              Close
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                onClose();
                onEdit(blog);
              }}
              className="text-xs flex items-center gap-1.5 px-4"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              <span>Edit Blog</span>
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BlogPreviewModal;
